// Library methods
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import DefaultModal from "src/components/shared/modals/DefaultModal/DefaultModal";
import SelectDropdown from "src/components/shared/inputs/SelectDropdown/SelectDropdown";
import { SelectChangeEvent } from "@mui/material/Select";

// Styles
import {
  ModalCustomCancel,
  ModalCustomConfirm,
} from "src/styles/mui/muiStylesHelper";

// Hooks
import { usePatientsData } from "src/services/patients/hooks/usePatientsData";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";
import { useExamSliderContext } from "src/contexts/exams/ExamSliderContext";

// Utils
import { getTruncatedText } from "src/utils/text/textHelper";

type SelectPatientModalType = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  modalDescription: string;
  selectedPatient: { displayName: string; value: string };
  setSelectedPatient: React.Dispatch<
    React.SetStateAction<{ displayName: string; value: string }>
  >;
};

const SelectPatientModal = ({
  open,
  onClose,
  onConfirm,
  modalDescription,
  selectedPatient,
  setSelectedPatient,
}: SelectPatientModalType) => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();

  const { actions } = useExamSliderContext();
  const { setCurrentPatient } = actions;

  const {
    data: patientsOptions,
    isLoading: isLoadingPatients,
    isFetching: isFetchingPatients,
    isRefetching: isRefetchingPatients,
  } = usePatientsData(clinicInfo?.id);

  const isLoading = useMemo(
    () => isLoadingPatients || isFetchingPatients || isRefetchingPatients,
    [isLoadingPatients, isFetchingPatients, isRefetchingPatients]
  );

  const formattedPatientsOptions = useMemo(
    () =>
      (patientsOptions ?? [])
        .map((option) => {
          const patientName = getTruncatedText(
            `${option?.firstName} ${option?.lastName}`
          );
          const birthdate = option?.dateOfBirth?.slice(0, 10) ?? "N/A";
          return {
            displayName: `${patientName} / ${birthdate}`,
            value: option.id,
          };
        })
        .sort((a, b) => a?.displayName?.localeCompare(b?.displayName)),
    [patientsOptions]
  );

  // disable the button if the selected patient isn't a valid dropdown option
  const shouldDisable = useMemo(() => {
    return !patientsOptions?.some(
      (patient) => patient?.id === selectedPatient.value
    );
  }, [patientsOptions, selectedPatient.value]);

  const handleSetPatient = useCallback(
    (event: SelectChangeEvent<any>) => {
      const value = formattedPatientsOptions?.find(
        (option) => option.displayName === event.target.value
      );

      if (value) {
        setSelectedPatient({ ...value });
      }
    },
    [formattedPatientsOptions, setSelectedPatient]
  );

  return (
    <DefaultModal
      isOpen={open}
      onClose={onClose}
      title={t("Select Patient")}
      maxWidth="xs"
      modalActions={
        <Grid
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
          }}
        >
          <Button sx={ModalCustomCancel} onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button
            sx={ModalCustomConfirm}
            disabled={shouldDisable}
            onClick={() => {
              const currentPatient = patientsOptions?.find(
                (patient) => patient.id === selectedPatient.value
              );
              if (currentPatient) {
                setCurrentPatient(currentPatient);
              }

              onConfirm();
            }}
          >
            {t("Confirm")}
          </Button>
        </Grid>
      }
    >
      <Grid display="flex" flexDirection="column" p="16px" gap="20px">
        <Typography>{modalDescription}</Typography>
        <SelectDropdown
          id="select-patient-dropdown"
          required
          disabled={isLoading}
          dropdownOptions={formattedPatientsOptions}
          value={selectedPatient.displayName}
          handleChange={handleSetPatient}
        />
      </Grid>
    </DefaultModal>
  );
};

export default SelectPatientModal;
